import { useState } from "react";
import { Field, inputCls, SmsConsent, RecaptchaNotice, useLeadSubmit } from "./shared";

const INTERESTS = ["Buying", "Selling", "Luxury Property", "Vacation Home", "Investment", "General Inquiry"];
const PROPERTY_TYPES = ["Single-Family Home", "Condominium", "Townhome", "Multi-Family", "Land", "Other"];
const TIMELINES = ["Immediately", "1–3 Months", "3–6 Months", "6+ Months", "Just Exploring"];

const LeadForm = ({ type, testIdPrefix }) => {
  const { submit, submitting } = useLeadSubmit(type);
  const empty = {
    first_name: "", last_name: "", name: "", email: "", phone: "", message: "",
    interest: "", destination: "", travel_dates: "", guests: "",
    property_address: "", property_type: "", estimated_timeline: "",
  };
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      first_name: type === "seller" ? form.name : form.first_name,
      last_name: type === "seller" ? "" : form.last_name,
      email: form.email,
      phone: form.phone,
      message: form.message,
      sms_consent: consent,
      details: {},
    };
    if (type === "contact") payload.details = { interest: form.interest };
    if (type === "vacation") payload.details = { destination: form.destination, travel_dates: form.travel_dates, number_of_guests: form.guests };
    if (type === "seller") payload.details = { property_address: form.property_address, property_type: form.property_type, estimated_timeline: form.estimated_timeline };
    const ok = await submit(payload);
    if (ok) {
      setForm(empty);
      setConsent(false);
    }
  };

  const ctaLabel = type === "contact" ? "Send Message" : type === "vacation" ? "Inquire Now" : "Request a Consultation";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid={`${testIdPrefix}-form`} noValidate={false}>
      {type === "seller" ? (
        <Field label="Name *" htmlFor={`${testIdPrefix}-name`}>
          <input id={`${testIdPrefix}-name`} required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid={`${testIdPrefix}-name-input`} />
        </Field>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="First Name *" htmlFor={`${testIdPrefix}-first`}>
            <input id={`${testIdPrefix}-first`} required value={form.first_name} onChange={set("first_name")} className={inputCls} placeholder="First name" data-testid={`${testIdPrefix}-first-name-input`} />
          </Field>
          <Field label="Last Name *" htmlFor={`${testIdPrefix}-last`}>
            <input id={`${testIdPrefix}-last`} required value={form.last_name} onChange={set("last_name")} className={inputCls} placeholder="Last name" data-testid={`${testIdPrefix}-last-name-input`} />
          </Field>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Email *" htmlFor={`${testIdPrefix}-email`}>
          <input id={`${testIdPrefix}-email`} type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid={`${testIdPrefix}-email-input`} />
        </Field>
        <Field label="Phone" htmlFor={`${testIdPrefix}-phone`}>
          <input id={`${testIdPrefix}-phone`} type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(___) ___-____" data-testid={`${testIdPrefix}-phone-input`} />
        </Field>
      </div>

      {type === "contact" && (
        <Field label="I'm Interested In" htmlFor={`${testIdPrefix}-interest`}>
          <select id={`${testIdPrefix}-interest`} value={form.interest} onChange={set("interest")} className={inputCls} data-testid={`${testIdPrefix}-interest-select`}>
            <option value="">Select an option</option>
            {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      )}

      {type === "vacation" && (
        <>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Destination" htmlFor={`${testIdPrefix}-destination`}>
              <input id={`${testIdPrefix}-destination`} value={form.destination} onChange={set("destination")} className={inputCls} placeholder="e.g. Cap Cana, Dominican Republic" data-testid={`${testIdPrefix}-destination-input`} />
            </Field>
            <Field label="Travel Dates" htmlFor={`${testIdPrefix}-dates`}>
              <input id={`${testIdPrefix}-dates`} value={form.travel_dates} onChange={set("travel_dates")} className={inputCls} placeholder="e.g. Dec 20 – Jan 3" data-testid={`${testIdPrefix}-dates-input`} />
            </Field>
          </div>
          <Field label="Number of Guests" htmlFor={`${testIdPrefix}-guests`}>
            <input id={`${testIdPrefix}-guests`} type="number" min="1" value={form.guests} onChange={set("guests")} className={inputCls} placeholder="e.g. 6" data-testid={`${testIdPrefix}-guests-input`} />
          </Field>
        </>
      )}

      {type === "seller" && (
        <>
          <Field label="Property Address" htmlFor={`${testIdPrefix}-address`}>
            <input id={`${testIdPrefix}-address`} value={form.property_address} onChange={set("property_address")} className={inputCls} placeholder="Street, City, State" data-testid={`${testIdPrefix}-address-input`} />
          </Field>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Property Type" htmlFor={`${testIdPrefix}-ptype`}>
              <select id={`${testIdPrefix}-ptype`} value={form.property_type} onChange={set("property_type")} className={inputCls} data-testid={`${testIdPrefix}-property-type-select`}>
                <option value="">Select a type</option>
                {PROPERTY_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Estimated Timeline" htmlFor={`${testIdPrefix}-timeline`}>
              <select id={`${testIdPrefix}-timeline`} value={form.estimated_timeline} onChange={set("estimated_timeline")} className={inputCls} data-testid={`${testIdPrefix}-timeline-select`}>
                <option value="">Select a timeline</option>
                {TIMELINES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>
        </>
      )}

      <Field label="Message" htmlFor={`${testIdPrefix}-message`}>
        <textarea id={`${testIdPrefix}-message`} rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} placeholder="Tell us a little about what you're looking for…" data-testid={`${testIdPrefix}-message-input`} />
      </Field>

      <SmsConsent checked={consent} onChange={setConsent} id={`${testIdPrefix}-sms-consent`} />

      <div className="flex flex-col gap-4 pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid={`${testIdPrefix}-submit-button`}
          className="inline-flex w-full items-center justify-center gap-3 bg-charcoal px-8 py-5 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-obsidian disabled:opacity-60 sm:w-auto"
        >
          {submitting ? "Sending…" : ctaLabel}
        </button>
        <RecaptchaNotice />
      </div>
    </form>
  );
};

export default LeadForm;
